// @file Handle picking attachments when composing a post
import { ContentPickerSource, SEARCH_MODE_BY_CONTENT_SOURCE } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { hasAnyAttachments, isPhotoAlbum } from '@@/bits/post_attachment'
import { SourceMenuScreen, useContentPickerStore } from '@@/pinia/content_picker'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceScreenRecorderStore } from '@@/pinia/surface_screen_recorder_store'
import type { AttachmentProps, WishAttachment, WishContent } from '@@/types'
import KeyboardShortcutTooltip from '@@/vuecomponents/KeyboardShortcutTooltip.vue'
import { useKeyboardShortcuts } from '@@/vuecomposables/keyboard_shortcuts'
import { getUndoDeleteShortcut, UNDO_TEXT } from '@@/vuecomposables/undoable'
import { usePhotoAlbumEditor } from '@@/vuecomposables/usePhotoAlbumEditor'
import type { Link } from '@padlet/arvo'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, shallowRef } from 'vue'

const useUndoableRemovePoll = (): {
  removePoll: typeof removePoll
  undoRemovingPoll: typeof undoRemovingPoll
  clearPollUndoState: typeof clearPollUndoState
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()

  const { activeDraft } = storeToRefs(surfaceDraftsStore)
  const { updateActiveDraft } = surfaceDraftsStore
  const { setSnackbar, removeSnackbar } = useGlobalSnackbarStore()

  const tempPoll = shallowRef<AttachmentProps>()
  const removePollSnackbarId = ref('')

  const clearPollUndoState = (): void => {
    tempPoll.value = undefined
    removeSnackbar(removePollSnackbarId.value)
    removePollSnackbarId.value = ''
  }

  const undoRemovingPoll = (): void => {
    if (tempPoll.value == null) return
    updateActiveDraft({ wish_content: { attachment_props: tempPoll.value } })
    clearPollUndoState()
  }

  const removePoll = (): void => {
    if (activeDraft.value?.wish_content?.attachment_props?.type !== 'poll') return
    tempPoll.value = activeDraft.value.wish_content.attachment_props
    updateActiveDraft({ wish_content: undefined })
    removePollSnackbarId.value = setSnackbar({
      message: __('Poll removed'),
      actionText: UNDO_TEXT,
      actionTextActions: [() => undoRemovingPoll()],
      actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
      actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
    })
  }

  onBeforeUnmount(() => {
    clearPollUndoState()
  })

  return {
    removePoll,
    undoRemovingPoll,
    clearPollUndoState,
  }
}

interface AttachmentPayload {
  attachment?: string
  attachment_caption?: string | null
  attachment_link?: Link | null
  attachments?: WishAttachment[]
  wish_content?: WishContent
}

const useUndoableRemoveNonPollAttachment = (): {
  removeNonPollAttachment: typeof removeNonPollAttachment
  undoRemovingAttachment: typeof undoRemovingAttachment
  clearAttachmentUndoState: typeof clearAttachmentUndoState
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const { activeDraft } = storeToRefs(surfaceDraftsStore)
  const { removeDraftAttachment, updateActiveDraft } = surfaceDraftsStore
  const { setSnackbar, removeSnackbar } = useGlobalSnackbarStore()
  const { deletePhotoAlbumImage, undoDeletePhotoAlbumImage } = usePhotoAlbumEditor()

  const tempAttachment = shallowRef<AttachmentPayload>()
  const removeAttachmentSnackbarId = ref('')

  const clearAttachmentUndoState = (): void => {
    tempAttachment.value = undefined
    removeSnackbar(removeAttachmentSnackbarId.value)
    removeAttachmentSnackbarId.value = ''
  }

  const undoRemovingAttachment = (): void => {
    if (tempAttachment.value == null) return

    if (isPhotoAlbum(activeDraft.value)) {
      undoDeletePhotoAlbumImage({ snackbarId: removeAttachmentSnackbarId.value, postCid: activeDraft.value.cid })
    } else {
      updateActiveDraft(tempAttachment.value)
    }
    clearAttachmentUndoState()
  }

  const removeNonPollAttachment = (index?: number): void => {
    if (activeDraft.value?.cid == null) return
    if (activeDraft.value?.wish_content?.attachment_props?.type === 'poll') return

    tempAttachment.value = {
      attachment: activeDraft.value?.attachment,
      attachment_caption: activeDraft.value?.attachment_caption ?? null,
      attachment_link: activeDraft.value?.attachment_link ?? null,
      attachments: isPhotoAlbum(activeDraft.value) ? activeDraft.value.attachments : undefined,
      wish_content: activeDraft.value?.wish_content ?? undefined,
    }

    if (isPhotoAlbum(activeDraft.value) && index !== undefined) {
      const result = deletePhotoAlbumImage({ index, postCid: activeDraft.value.cid })
      if (result) {
        removeAttachmentSnackbarId.value = result.snackbarId
      }
    } else {
      removeDraftAttachment(activeDraft.value.cid)
      removeAttachmentSnackbarId.value = setSnackbar({
        message: __('Attachment removed'),
        actionText: UNDO_TEXT,
        actionTextActions: [() => undoRemovingAttachment()],
        actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
        actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
      })
    }
  }

  onBeforeUnmount(() => {
    clearAttachmentUndoState()
  })

  return {
    removeNonPollAttachment,
    undoRemovingAttachment,
    clearAttachmentUndoState,
  }
}

const AttachmentTypes = {
  POLL: 'poll',
  NON_POLL: 'non-poll',
  PHOTO_ALBUM: 'photo-album',
} as const

type AttachmentType = (typeof AttachmentTypes)[keyof typeof AttachmentTypes]

export const useSurfaceAttachmentPicker = (): {
  pickContentSourceIfSupported: typeof pickContentSourceIfSupported
  editPoll: typeof editPoll
  uploadFile: typeof uploadFile
  removeAttachment: typeof removeAttachment
  cancelLoadAttachment: typeof cancelLoadAttachment
  registerUndoRemoveAttachmentKeyboardShortcut: typeof registerUndoRemoveAttachmentKeyboardShortcut

  updateAltTextInActiveDraft: typeof updateAltTextInActiveDraft
  draftAttachmentAlternativeText: typeof draftAttachmentAlternativeText
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()

  const { activeDraft } = storeToRefs(surfaceDraftsStore)
  const { uploadFileActiveDraft, stopUploadFileActiveDraft } = surfaceDraftsStore
  const { showContentSourceMenu, startLinkInputMode, setSourceMenuScreen, pickContentSource } = useContentPickerStore()
  const { showPostContentPicker } = useSurfaceContentPickerStore()
  const surfaceScreenRecorderStore = useSurfaceScreenRecorderStore()
  const { showPhotoAlbumImagePicker, currentItemIndex: photoAlbumAttachmentIndex } = usePhotoAlbumEditor()

  const lastRemovedAttachmentType = ref<AttachmentType | null>(null)

  const hasAttachment = computed(
    () => Boolean(activeDraft.value?.attachment) || activeDraft.value?.wish_content?.attachment_props != null,
  )

  const focusEditorAfterDone = (): void => {
    // TODO: implement
  }

  const pickContentSourceIfSupported = (source?: ContentPickerSource | 'see_all'): void => {
    showPostContentPicker()

    // If no source is provided, open the content source menu to let the user choose
    if (source == null || source === 'see_all') {
      showContentSourceMenu()
      focusEditorAfterDone()
      return
    }

    if (source === ContentPickerSource.ScreenshareRecorder) {
      surfaceScreenRecorderStore.saveCopyOfCurrentDraft()
      surfaceScreenRecorderStore.showScreenRecorder()
      return
    }

    if (source === ContentPickerSource.FileBrowser || (source === ContentPickerSource.PhotoBooth && device.mobile)) {
      // These sources are handled by the hidden file input in @padlet/universal-post-editor#AttachmentPickerButton.vue
      return
    }

    if (source === ContentPickerSource.LinkInput) {
      showContentSourceMenu()
      focusEditorAfterDone()
      startLinkInputMode()
      return
    }

    if (source === ContentPickerSource.AiImageSearch) {
      showContentSourceMenu()
      setSourceMenuScreen(SourceMenuScreen.AIImagePreview)
      return
    }

    if (source === ContentPickerSource.PhotoAlbum) {
      showPhotoAlbumImagePicker()
      return
    }

    const searchFilter = SEARCH_MODE_BY_CONTENT_SOURCE[source]
    if (searchFilter != null) {
      showContentSourceMenu({ searchFilter, search: true })
      focusEditorAfterDone()
      // TODO: focus the search input
      return
    }

    pickContentSource({ source })
  }

  const editPoll = (): void => {
    showPostContentPicker()
    pickContentSource({ source: ContentPickerSource.Poll })
  }

  const draftAttachmentAlternativeText = computed<string>(() => {
    if (isPhotoAlbum(activeDraft.value)) {
      // For multiple attachments, get the alt text from the current attachment
      const currentAttachment = activeDraft.value.attachments[photoAlbumAttachmentIndex.value]
      return currentAttachment?.alt ?? ''
    }
    // For single attachment (backward compatibility)
    return activeDraft.value?.wish_content?.attachment_props?.alternative_text ?? ''
  })

  const updateAltTextInActiveDraft = (text: string): void => {
    if (isPhotoAlbum(activeDraft.value)) {
      const attachments = [...activeDraft.value.attachments]
      attachments[photoAlbumAttachmentIndex.value] = {
        ...attachments[photoAlbumAttachmentIndex.value],
        alt: text,
      }
      surfaceDraftsStore.updateDraft({
        attachments,
        cid: activeDraft.value.cid,
      })
    } else {
      surfaceDraftsStore.updateActiveDraft({
        wish_content: {
          attachment_props: { ...(activeDraft.value?.wish_content?.attachment_props ?? {}), alternative_text: text },
        },
      })
    }
  }

  const uploadFile = (e: Event): void => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (file != null) uploadFileActiveDraft({ file })
  }

  const { removePoll, undoRemovingPoll, clearPollUndoState } = useUndoableRemovePoll()
  const { removeNonPollAttachment, undoRemovingAttachment, clearAttachmentUndoState } =
    useUndoableRemoveNonPollAttachment()

  const registerUndoRemoveAttachmentKeyboardShortcut = (): void => {
    useKeyboardShortcuts([
      {
        key: '$mod+KeyZ',
        callback: (event: KeyboardEvent) => {
          if (lastRemovedAttachmentType.value == null) return

          // Prevent the event from bubbling up to other undo handlers
          event.stopImmediatePropagation()
          event.preventDefault()

          switch (lastRemovedAttachmentType.value) {
            case AttachmentTypes.POLL:
              if (hasAttachment.value) {
                clearPollUndoState()
              } else {
                undoRemovingPoll()
              }
              break
            case AttachmentTypes.NON_POLL:
              if (hasAttachment.value) {
                clearAttachmentUndoState()
              } else {
                undoRemovingAttachment()
              }
              break
            case AttachmentTypes.PHOTO_ALBUM:
              undoRemovingAttachment()
              break
            default:
              // Unexpected attachment type, do nothing
              return
          }
          lastRemovedAttachmentType.value = null
        },
      },
    ])
  }

  const removeAttachment = (index?: number): void => {
    if (activeDraft.value?.cid == null) return

    // Early return if there's no attachment to remove
    // This prevents unnecessary operations when multiple remove calls happen
    // (e.g. attachment-cancel + attachment-remove)
    if (!hasAnyAttachments(activeDraft.value)) return

    if (activeDraft.value?.wish_content?.attachment_props?.type === 'poll') {
      lastRemovedAttachmentType.value = AttachmentTypes.POLL
      removePoll()
    } else if (isPhotoAlbum(activeDraft.value) && index !== undefined) {
      lastRemovedAttachmentType.value = AttachmentTypes.PHOTO_ALBUM
      removeNonPollAttachment(index)
    } else {
      lastRemovedAttachmentType.value = AttachmentTypes.NON_POLL
      removeNonPollAttachment()
    }
  }

  const cancelLoadAttachment = (): void => {
    if (activeDraft.value?.uploadingFile != null) {
      stopUploadFileActiveDraft()
    } else {
      removeAttachment()
    }
  }

  return {
    pickContentSourceIfSupported,
    editPoll,
    uploadFile,
    removeAttachment,
    cancelLoadAttachment,
    registerUndoRemoveAttachmentKeyboardShortcut,

    draftAttachmentAlternativeText,
    updateAltTextInActiveDraft,
  }
}
